import { useApi } from '@backstage/core-plugin-api';
import { CircularProgress, Grid, TextField } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { FieldValidation } from '@rjsf/utils';

import { useAsync, useAsyncFn } from 'react-use';
import { unifiedGithubApiRef } from '../../../services';
import { GithubOrgPrefix } from '../../../components/GithubOrgPrefix';
import type { GithubDestination } from '../../../services/unifiedGithubService/unifiedGithubService.types';

export const GithubRepoField = (props: {
  error: boolean;
  onChange: (value: string) => void;
  value?: string;
  /**
   * Fixed organization the repository will be created in. When provided, the
   * prefix never changes and availability is checked for this organization
   * (plus the other organization in combined mode). When omitted, the legacy
   * feature-flag routing is used and the prefix follows the lookup result.
   */
  destination?: GithubDestination;
}) => {
  const { error, onChange, value, destination } = props;

  const unifiedGithubApi = useApi(unifiedGithubApiRef);
  // Callers without a fixed destination also need a prefix for saved, prefilled names.
  const repoLookupState = useAsync(async () => {
    if (value && !destination) {
      return { repo: value, ...(await unifiedGithubApi.lookupRepo({ repoName: value })) };
    }
    return undefined;
  }, [unifiedGithubApi, value, destination]);
  const [validateRepoNameState, validateRepoName] = useAsyncFn(
    async (repo: string) => {
      const regexp = new RegExp('^[a-zA-Z][a-z0-9-.A-Z_]*$');
      const containerNameCriteriaRegex = new RegExp('^[a-z0-9]+(?:[\\/._-]{1}[a-z0-9]+)*$');
      if (!regexp.test(repo)) {
        throw new Error(
          'Please use letters, dashes, underscores, periods, and numbers. You must start and end with a letter.',
        );
      } else if (repo.length > 56) {
        throw new Error('Names cannot be more than 56 characters.');
      }

      if (!containerNameCriteriaRegex.test(repo)) {
        throw new Error('Only lowercase alphanumeric characters, -, _ are allowed');
      }

      let repoLookup;
      try {
        repoLookup = destination
          ? await unifiedGithubApi.lookupRepoForDestination({
              repoName: repo,
              owner: destination.organization,
              origin: destination.origin,
            })
          : await unifiedGithubApi.lookupRepo({ repoName: repo });
      } catch (lookupError) {
        if (destination) {
          throw new Error(
            `Unable to check repository availability in ${destination.organization}. Confirm you are signed in to GitHub with access to ${destination.organization}, then try again. (${
              lookupError instanceof Error ? lookupError.message : String(lookupError)
            })`,
          );
        }
        throw lookupError;
      }

      let availabilityError: string | undefined;
      if (repoLookup.exists) {
        availabilityError = 'Repo Name is taken!';
        if (destination && repoLookup.owner.toLowerCase() !== destination.organization.toLowerCase()) {
          availabilityError = `Repo Name is taken! It already exists in ${repoLookup.owner}, and names must be unique across organizations during the migration.`;
        }
      }

      return {
        repo,
        owner: repoLookup.owner,
        message: destination ? `Repo Name is available in ${destination.organization}!` : 'Repo Name is unique!',
        error: availabilityError,
      };
    },
    [destination, unifiedGithubApi],
  );

  // Do not display a lookup result belonging to a previous repository name.
  const validationResult =
    value === undefined || validateRepoNameState.value?.repo === value ? validateRepoNameState.value : undefined;
  const prefilledRepo = repoLookupState.value?.repo === value ? repoLookupState.value : undefined;
  const organization = destination?.organization ?? validationResult?.owner ?? prefilledRepo?.owner ?? 'AAInternal';
  const lookupError = destination ? undefined : repoLookupState.error;
  const validationError = validateRepoNameState.error?.message ?? validationResult?.error ?? lookupError?.message;

  return (
    <Grid container>
      <Grid item xs={6}>
        <TextField
          id="repo-name"
          label="Repo Name"
          style={{ width: '100%' }}
          required
          variant="outlined"
          onChange={({ target: { value: newValue } }) => {
            onChange(newValue);
          }}
          value={value}
          onBlur={({ target: { value: newValue } }) => {
            if (newValue) validateRepoName(newValue);
          }}
          InputProps={{ startAdornment: <GithubOrgPrefix organization={organization} /> }}
          error={!!(error || validationError)}
        />
      </Grid>
      {validateRepoNameState.loading && (
        <Grid item xs={1}>
          <CircularProgress style={{ padding: 5 }} />
        </Grid>
      )}
      {validationError && !validateRepoNameState.loading && (
        <Grid item xs={3}>
          <Alert severity="error">{validationError}</Alert>
        </Grid>
      )}
      {validationResult?.message && !validationError && !validateRepoNameState.loading && (
        <Grid item xs={3}>
          <Alert severity="success">{validationResult.message}</Alert>
        </Grid>
      )}
    </Grid>
  );
};

export const repoNameValidation = (value: string, validation: FieldValidation) => {
  if (!value) {
    validation.addError('Please enter a repo name');
  }
};

export const GithubRepoDescription = (props: { error: boolean; onChange: (value: string) => void; value?: string }) => {
  const { error, onChange, value } = props;

  return (
    <TextField
      id="description"
      label="Repo Description"
      style={{ width: '100%' }}
      required
      variant="outlined"
      aria-label="repo-description"
      value={value}
      fullWidth
      onChange={({ target: { value: newValue } }) => {
        onChange(newValue);
      }}
      error={error}
    />
  );
};

export const repoDescriptionValidation = (value: string, validation: FieldValidation) => {
  if (!value) {
    validation.addError('Please enter a repo description');
  } else if (/[<>]/.test(value)) {
    validation.addError('Please remove angle brackets (< or >) from the repo description');
  }
};
